"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";

import Link from "next/link";
import classes from "./ProductCard.module.css";

import onlyOnly from "@/assets/svgs/online-only.svg";
import newArrival from "@/assets/svgs/new-arrival.svg";

import b1 from "@/assets/b1.svg";
import b2 from "@/assets/b2.png";
import b3 from "@/assets/b3.png";
import b4 from "@/assets/b4.png";
import { convertNameChar, getImageArray, numberWithCommas } from "@/utils";
import { getS3ImageUrl } from "@/utils/routes";
import RatingStars from "../RatingStars";
import Button from "../buttons/Button";
import { formatImagePath } from "@/utils/split";

const brands = [b1.src, b2.src, b3.src, b4.src];

interface Props {
  product?: ProductType;
  type: "xl" | "2lg" | "lg" | "md" | "sm" | "xs" | "full";
  cart?: boolean;
  wishlist?: boolean;
  quantity?: number;
  selectedAttributes?: {
    name: string;
    value: string;
  }[];
  onRemove?: () => void;
  onClose?: () => void;
  onQuantityChange?: (quantity: number) => void;
  isOnCart?: boolean;
  outOfStock?: boolean;
  isHighQuantity?: boolean;
  exededQuantity?: number;
  availableQuantity?: number;
  campaign_slug?: string;

  productItem?: ProductItem;
  settings?: WidgetSettings;
}

interface ClassNameProps {
  className?: string;
}

interface PriceTagProps extends ClassNameProps {
  price: number;
  previousPrice?: number;
  reverse?: boolean;
  vat?: number;
  size?: "md" | "lg";
}

export const CardPriceTag = ({
  price,
  previousPrice,
  vat,
  className,
  reverse,
  size = "md",
}: PriceTagProps) => {
  const classNames = [classes.CardPriceTag];
  if (reverse) classNames.push(classes.reverse);
  if (size === "lg") classNames.push(classes.lg);

  // return (
  //   <p className={`${classNames.join(" ")} ${className}`}>
  //     <span className={classes.price}>Tk. {numberWithCommas(price)}</span>

  return (
    <p className={`${classNames.join(" ")} ${className}`}>
      <span className={classes.price}>
        Tk. {numberWithCommas(Math.ceil(price))}
      </span>

      {!!previousPrice && previousPrice > price && (
        <span className={classes.prePrice}>
          <span></span>
          Tk. {numberWithCommas(Math.ceil(previousPrice))}
        </span>
      )}

      {/* {!!previousPrice && previousPrice > price && (
        <span className={classes.prePrice}>
          <span></span>
          Tk. {numberWithCommas(previousPrice)}
        </span>
      )} */}

      {!!vat && <span className="text-xs text-text-gray4">(incl VAT)</span>}
    </p>
  );
  //     {!!vat && <span className="text-xs text-text-gray4">(incl VAT)</span>}
  //   </p>
  // );
};

export const ProductTitle = ({
  title,
  className,
}: ClassNameProps & { title: string }) => {
  return (
    <h4
      className={`${className} ${classes.cardTitle}`}
      title={title}
      style={{ height: "44px" }}
    >
      {title}
    </h4>
  );
};

const ProductCard = ({
  product: _product,
  type,
  cart = false,
  wishlist = false,
  isOnCart = false,
  quantity = 1,
  selectedAttributes,
  onRemove,
  onClose = () => {},
  outOfStock,
  isHighQuantity,
  exededQuantity,
  availableQuantity,
  campaign_slug,
  onQuantityChange = () => {},

  productItem,
  settings,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isOutOfStock, setIsOutOfStock] = useState(false);
  const [comingSoon, setComingSoon] = useState(false);
  const [preOrder, setPreOrder] = useState(false);

  useEffect(() => {
    if (!!productItem?.skus?.length) {
      setIsOutOfStock(
        productItem?.stockStatus === "Stock Out" ||
          productItem?.skus?.every((sku) => sku.stockQuantity <= 0),
      );
    }

    if (productItem?.stockStatus === "Coming Soon") {
      setComingSoon(true);
    }
    if (productItem?.stockStatus === "Pre Order") {
      setPreOrder(true);
    }
  }, [productItem?.skus, productItem?.comingSoon, productItem?.stockStatus]);

  const cardClasses = [classes.ProductCard];

  switch (type) {
    case "full":
      cardClasses.push(classes.full);
      break;
    case "xl":
      cardClasses.push(classes.xl);
      break;
    case "2lg":
      cardClasses.push(classes.lg2);
      break;
    case "lg":
      cardClasses.push(classes.lg);
      break;
    case "md":
      cardClasses.push(classes.md);
      break;
    case "sm":
      cardClasses.push(classes.sm);
      break;
    case "xs":
      cardClasses.push(classes.xs);
      break;
  }

  if (cart) cardClasses.push(classes.cart);
  if (wishlist) cardClasses.push(classes.wishlist);
  if (isOutOfStock) cardClasses.push(classes.outOfStock);

  const product = productItem;

  const skus = useMemo(() => {
    if (product?.skus) {
      return product.skus;
    }
  }, [product?.skus]);

  if (!product) return null;

  const productUrl = `/product/${product.slug || product.id}`;

  return (
    <Link href={productUrl} prefetch={false} scroll={true}>
      <div
        ref={ref}
        className={cardClasses.join(" ")}
        onClick={(e) => {
          const target = e.target as HTMLElement;

          // noclick elements (buttons etc.)
          if (
            target?.classList.contains("noclick") ||
            target?.closest(".noclick")
          ) {
            return;
          }

          onClose?.();
        }}
      >
        <div
          className={classes.cardTop}
          style={{
            // height: type === 'xs' && !!rect?.height ? rect?.height + 'px' : undefined,
            height: type === "xs" ? "100%" : undefined,
          }}
        >
          <Image
            className={classes.cardImage}
            src={
              getS3ImageUrl(
                getImageArray(product.thumbnail)[0] || "",
                "MEDIA",
              ) ||
              "https://scotturb.com/wp-content/uploads/2016/11/product-placeholder.jpg"
            }
            alt={product.name}
            blurDataURL={
              "https://www.unfe.org/wp-content/uploads/2019/04/SM-placeholder.png"
            }
            placeholder="blur"
            layout="responsive"
            width={type !== "full" ? 500 : undefined}
            height={type !== "full" ? 500 : undefined} // {type !== 'xl' ? 400 : 500}
            unoptimized={true}
            style={{
              aspectRatio: type === "full" ? undefined : 1,
              objectFit: "contain",
            }}
          />

          {!!productItem?.isOnline && (
            <div className="absolute left-0 top-0 z-50">
              <Image
                src={newArrival}
                alt="online only"
                width={500}
                height={500}
                className={`${type === "xs" ? "w-12 h-12" : "w-16 h-16"}`}
                style={{
                  objectFit: "contain",
                  aspectRatio: 500 / 500,
                }}
                unoptimized={true}
              />
            </div>
          )}

          {!!productItem?.isHot && (
            <div className="absolute left-[50%] lg:left-[45%] top-1 z-50 flex -translate-x-1/2 items-center gap-0 lg:gap-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 px-1 lg:px-2.5 py-1 font-bold uppercase text-white shadow-[0_6px_16px_rgba(239,68,68,0.25)]">
              {" "}
              <span className="text-[8px] lg:text-[10px] leading-none">🔥</span>{" "}
              <span className="text-[8px] lg:text-[10px] leading-none">Hot</span>{" "}
            </div>
          )}

          {!!productItem?.isSale && (
            <div className="absolute right-0 top-1 z-50 flex items-center gap-0 lg:gap-1 rounded-full bg-gradient-to-r from-primary via-pink-400 to-pink-500 px-1 lg:px-2 font-bold uppercase text-white shadow-[0_12px_24px_rgba(0,0,0,0.15)] py-1">
              <span className="text-[8px] lg:text-[10px] leading-none">⚡</span>
              <span className="leading-none text-[8px] lg:text-[10px]">Top Sale</span>
            </div>
          )}

          {/* {!!productItem?.isOnline && (
          <Link href={productUrl} className="absolute right-2 top-2 z-50">
            <Image
              src={onlyOnly}
              alt="online only"
              width={500}
              height={500}
              className={`${type === "xs" ? "w-12 h-12" : "w-16 h-16"}`}
              style={{
                objectFit: "contain",
                aspectRatio: 500 / 500,
              }}
              unoptimized={true}
            />
          </Link>
        )} */}

          {/* {isOutOfStock && (
					<Link href={productUrl}>
						<div className={classes.outOfStock}>
							<p>Out of stock !</p>
						</div>
					</Link>
				)} */}
        </div>
        {/* </div> */}

        <div className={classes.cardBody}>
          <div className={classes.top}>
            <div className={classes.cardTextContents}>
              {type !== "xs" && (
                <button className={classes.brandButton}>
                  {product?.brand?.logo ? (
                    <Image
                      src={getS3ImageUrl(formatImagePath(product?.brand?.logo))}
                      alt="brand"
                      layout="fill"
                      objectFit="contain"
                      className={classes.image}
                      unoptimized={true}
                    />
                  ) : (
                    <span className="uppercase border border-primary text-sm px-1 py-0.5 rounded-sm">
                      {product?.brand?.name}
                    </span>
                  )}
                </button>
              )}

              <ProductTitle
                className={classes.cardTitle}
                title={convertNameChar(product.name)}
              />
              <div className="h-[1rem] lg:h-[1.5rem] w-[50%] pb-10">
                {skus &&
                  skus[0]?.discountedPrice > 0 &&
                  skus[0]?.price > skus[0]?.discountedPrice && (
                    <div className=" bg-primary/10 rounded-md flex items-center justify-center border border-primary text-primary  font-semibold ">
                      <div className="text-center font-semibold text-primary text-[10px] lg:text-[12px]">
                        SAVE <span className="text-[16px]">৳</span>{" "}
                        {Math.ceil(skus[0].price - skus[0].discountedPrice)}
                      </div>
                    </div>
                  )}
              </div>

              {preOrder ? (
                // <p className={classes.preOrderText}>Pre Order !</p>
                <CardPriceTag
                  className={classes.cardPriceTag}
                  price={skus?.[0]?.discountedPrice || skus?.[0]?.price || 0}
                  previousPrice={skus?.[0]?.price || 0}
                />
              ) : comingSoon ? (
                <>
                  {/* <p className={classes.comingSoon}>Coming Soon !</p> */}
                  <CardPriceTag
                    className={classes.cardPriceTag}
                    price={skus?.[0]?.discountedPrice || skus?.[0]?.price || 0}
                    previousPrice={skus?.[0]?.price || 0}
                  />
                </>
              ) : outOfStock ? (
                <p className={classes.outOfStockText}>Out of stock !</p>
              ) : (
                <CardPriceTag
                  className={classes.cardPriceTag}
                  price={skus?.[0]?.discountedPrice || skus?.[0]?.price || 0}
                  previousPrice={skus?.[0]?.price || 0}
                />
              )}
              {/* {comingSoon ? (
                <p className={classes.comingSoon}>Coming Soon !</p>
              ) : outOfStock ? (
                <p className={classes.outOfStockText}>Out of stock !</p>
              ) : (
                <CardPriceTag
                  className={classes.cardPriceTag}
                  price={skus?.[0]?.discountedPrice || skus?.[0]?.price || 0}
                  previousPrice={skus?.[0]?.price || 0}
                />
              )} */}

              {product?.review?.totalReview &&
              product.review?.totalReview > 0 ? (
                <RatingStars
                  rating={product.review.avgRating as number}
                  size="sm"
                  className="py-0.5 sm:py-2"
                />
              ) : (
                <p className="text-[0.75rem] md:text-[0.83rem] text-primary-dark/80 font-semibold !mt-2 h-[15px] md:h-[30px]">
                  {/* No Review Yet */}
                </p>
              )}

              {cart && selectedAttributes && (
                <div className={classes.attributes}>
                  {selectedAttributes.map((attribute, index) => (
                    <p
                      key={index + "-attribute-" + attribute.value}
                      className={classes.attribute}
                    >
                      {attribute.value}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>

          {type !== "xs" && type !== "sm" && (
            <CardButtons
              productUrl={productUrl}
              preOrder={preOrder}
              outOfStock={outOfStock}
              comingSoon={comingSoon}
            />
          )}
        </div>
      </div>
    </Link>
  );
};

export const CardButtons = ({
  type = "full",
  productUrl,
  preOrder,
  outOfStock,
  comingSoon,
}: {
  type?: "full" | "md";
  productUrl: string;
  preOrder?: boolean;
  outOfStock?: boolean;
  comingSoon?: any;
}) => {
  return (
    <div className="flex gap-2 mt-2">
      {preOrder ? (
        <span
          className={`${type === "full" ? "w-full" : "px-6"} no-click bg-primary/10 text-primary hover:bg-primary hover:text-primary-white py-1 md:py-1.5 px-0.5 text-center text-[0.75rem] md:text-[0.85rem] font-medium transition-colors duration-500 border !border-primary ${
            classes.cardButton
          } !rounded-full`}
        >
          Pre Order
        </span>
      ) : comingSoon ? (
        <>
          <span
            className={`${type === "full" ? "w-full" : "px-6"} no-click bg-gray-100 text-gray-600 py-1 md:py-1.5 px-0.5 text-center text-[0.75rem] md:text-[0.85rem] font-medium transition-colors duration-500 ${
              classes.cardButton
            } !rounded-full`}
          >
            Coming Soon!
          </span>
        </>
      ) : outOfStock ? (
        <span
          className={`${type === "full" ? "w-full" : "px-6"} no-click bg-gray-100 text-gray-600  py-1 md:py-1.5 px-0.5 text-center text-[0.75rem] md:text-[0.85rem] font-medium transition-colors duration-500 ${
            classes.cardButton
          } !rounded-full`}
        >
          Out Of Stock!
        </span>
      ) : (
        <span
          className={`${type === "full" ? "w-full" : "px-6"} no-click bg-primary/10 text-primary hover:bg-primary hover:text-primary-white py-1 md:py-1.5 px-0.5 text-center text-[0.75rem] md:text-[0.85rem] font-medium transition-colors duration-500 border !border-primary ${
            classes.cardButton
          } !rounded-full`}
        >
          Buy Now
        </span>
      )}
      {/* <Button
        className={`${type === "full" ? "w-full" : "px-6"} no-click ${
          classes.cardButton
        } !rounded-md`}
        btnType="gray"
        onClick={() => {}}
      >
        Add to Cart
      </Button> */}
    </div>
  );
};

export default ProductCard;
